import { useEffect, useState } from 'react';
import type { IKanbanCard } from '../../hooks/useKanban';

/** Minutos que o card fica marcado como "Novo" depois de entrar na coluna (user, 23/09). */
export const MINUTOS_DE_NOVO = 30;

type CardComChegada = Pick<IKanbanCard, 'moved_at' | 'dt_criacao' | 'dias_etapa_atual'>;

// A API manda `moved_at` sem fuso, mas é UTC (o `dt_criacao` do mesmo card vem com "Z").
// Sem o "Z", o navegador leria como hora local e erraria 3 h.
export const instanteDeChegada = (card: CardComChegada): number | null => {
  const bruto = card.moved_at || card.dt_criacao;
  if (!bruto) return null;
  const comFuso = /(z|[+-]\d\d:?\d\d)$/i.test(bruto) ? bruto : `${bruto}Z`;
  const instante = Date.parse(comFuso);
  return Number.isNaN(instante) ? null : instante;
};

/** Horas desde que o card entrou na coluna atual. */
export const horasNaColuna = (card: CardComChegada, agora = Date.now()): number | null => {
  const instante = instanteDeChegada(card);
  if (instante !== null) return Math.max(0, (agora - instante) / 3_600_000);
  return card.dias_etapa_atual != null ? card.dias_etapa_atual * 24 : null;
};

export const ehNovo = (card: CardComChegada, agora = Date.now()): boolean => {
  const horas = horasNaColuna(card, agora);
  return horas !== null && horas * 60 < MINUTOS_DE_NOVO;
};

/** "chegou há 5 min", "chegou há 3 h", "na etapa há 2 dias". */
export const textoDaChegada = (card: CardComChegada, agora = Date.now()): string | null => {
  const horas = horasNaColuna(card, agora);
  if (horas === null) return null;
  if (horas < 1) return `chegou há ${Math.max(1, Math.floor(horas * 60))} min`;
  if (horas < 24) return `chegou há ${Math.floor(horas)} h`;
  const dias = Math.floor(horas / 24);
  return `na etapa há ${dias} dia${dias > 1 ? 's' : ''}`;
};

/** Mais recente primeiro. */
export const porChegada = (a: CardComChegada, b: CardComChegada): number =>
  (instanteDeChegada(b) ?? 0) - (instanteDeChegada(a) ?? 0);

/**
 * Relógio da tela: o board não recarrega sozinho, e sem isso o "Novo" ficaria até a próxima
 * recarga em vez dos 30 min.
 */
export const useAgora = (ativo: boolean, intervaloMs = 60_000): number => {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return undefined;
    const id = window.setInterval(() => setAgora(Date.now()), intervaloMs);
    return () => window.clearInterval(id);
  }, [ativo, intervaloMs]);
  return agora;
};
